import React, { useState, useEffect } from 'react';
import { Modal, Spin, Image, Checkbox, Button, Pagination, message, Empty, Input } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import './index.css';
import { useTranslation } from '../../hooks/useTranslation';
import { isImg } from '../../helpers/img';

interface ImageSelectorProps {
  open: boolean;
  onClose: () => void;
  onSelect: (selectedUrls: string[]) => void;
  onSelectRecords?: (records: ImageSelectorRecord[]) => void;
  acceptedContentTypes?: string[];
  currentImages: string[]; // 当前已选中的图片（用于高亮显示）
}

export interface ImageSelectorRecord {
  id: number;
  url: string;
  fileName: string;
  contentType: string;
  time: string;
}

const ImageSelector: React.FC<ImageSelectorProps> = ({
  open,
  onClose,
  onSelect,
  onSelectRecords,
  acceptedContentTypes,
  currentImages
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState<ImageSelectorRecord[]>([]);
  const [selectedUrls, setSelectedUrls] = useState<string[]>([]);
  const [selectedRecords, setSelectedRecords] = useState<Map<string, ImageSelectorRecord>>(new Map());
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [keyword, setKeyword] = useState('');

  const loadHistory = async (currentPage: number = page, searchKeyword: string = keyword) => {
    setLoading(true);
    try {
      const uid = localStorage.getItem('fdd_uid') || '';
      const response = await fetch(
        `/img/history?page=${currentPage}&pageSize=${pageSize}&keyword=${encodeURIComponent(searchKeyword)}&uid=${uid}`,
        {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        }
      );

      const result = await response.json();
      console.info('result is', result)
      if (result.data) {
        // 只保留图片类型的记录
        const imageRecords = result.data.filter((record: ImageSelectorRecord) =>
          isImg(record.fileName || record.url)
          && (!acceptedContentTypes || acceptedContentTypes.includes(record.contentType))
        );
        setRecords(imageRecords);
        setTotal(result.total);
      } else {
        message.error(t('imageSelector.loadFailed', { message: result.message || t('common.unknown') }));
      }
    } catch (error) {
      message.error(t('imageSelector.loadFailedNetwork'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadHistory(1, '');
      setSelectedUrls([]);
      setSelectedRecords(new Map());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // 切换选中状态
  const toggleSelect = (record: ImageSelectorRecord) => {
    if (selectedUrls.includes(record.url)) {
      setSelectedUrls(selectedUrls.filter(u => u !== record.url));
      setSelectedRecords((current) => {
        const next = new Map(current);
        next.delete(record.url);
        return next;
      });
    } else {
      setSelectedUrls([...selectedUrls, record.url]);
      setSelectedRecords((current) => new Map(current).set(record.url, record));
    }
  };

  // 全选/取消全选当前页
  const handleSelectAll = () => {
    const currentPageUrls = records.map(r => r.url);
    const allSelected = currentPageUrls.every(url => selectedUrls.includes(url));

    if (allSelected) {
      // 取消全选当前页
      setSelectedUrls(selectedUrls.filter(url => !currentPageUrls.includes(url)));
      setSelectedRecords((current) => {
        const next = new Map(current);
        currentPageUrls.forEach((url) => next.delete(url));
        return next;
      });
    } else {
      // 全选当前页
      const newSelected = [...selectedUrls];
      currentPageUrls.forEach(url => {
        if (!newSelected.includes(url)) {
          newSelected.push(url);
        }
      });
      setSelectedUrls(newSelected);
      setSelectedRecords((current) => {
        const next = new Map(current);
        records.forEach((record) => next.set(record.url, record));
        return next;
      });
    }
  };

  // 确认选择
  const handleConfirm = () => {
    if (selectedUrls.length === 0) {
      message.warning(t('imageSelector.selectAtLeastOne'));
      return;
    }
    onSelect(selectedUrls);
    onSelectRecords?.(selectedUrls
      .map((url) => selectedRecords.get(url))
      .filter((record): record is ImageSelectorRecord => Boolean(record)));
    onClose();
  };

  // 搜索
  const handleSearch = () => {
    setPage(1);
    loadHistory(1, keyword);
  };

  // 分页切换
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    loadHistory(newPage, keyword);
  };

  return (
    <Modal
      title={t('imageSelector.title', { count: selectedUrls.length })}
      open={open}
      onCancel={onClose}
      onOk={handleConfirm}
      width={900}
      style={{ top: 20 }}
      bodyStyle={{ maxHeight: '70vh', overflowY: 'auto', paddingTop: 16, paddingBottom: 16 }}
      destroyOnClose
    >
      <div className="image-selector">
        {/* 搜索栏 */}
        <div className="search-bar">
          <Input
            placeholder={t('imageSelector.searchPlaceholder')}
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            onPressEnter={handleSearch}
            prefix={<SearchOutlined />}
            allowClear
            size="small"
          />
          <Button type="primary" size="small" onClick={handleSearch} style={{ marginLeft: 8 }}>
            {t('imageSelector.search')}
          </Button>
          <Button size="small" onClick={handleSelectAll} style={{ marginLeft: 8 }}>
            {records.every(r => selectedUrls.includes(r.url)) ? t('imageSelector.deselectAll') : t('imageSelector.selectAll')}
          </Button>
        </div>

        {/* 加载状态 */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Spin size="large" />
            <p>{t('imageSelector.loading')}</p>
          </div>
        ) : records.length === 0 ? (
          <Empty description={t('imageSelector.noImages')} />
        ) : (
          <>
            {/* 图片网格 */}
            <div className="image-selector-grid">
              {records.map((record) => {
                const isSelected = selectedUrls.includes(record.url);
                const isInGallery = currentImages.includes(record.url);

                return (
                  <div
                    key={record.id}
                    className={`image-selector-item ${isSelected ? 'selected' : ''} ${isInGallery ? 'in-gallery' : ''}`}
                    onClick={() => toggleSelect(record)}
                  >
                    <Image
                      src={record.url}
                      alt={record.fileName}
                      width="100%"
                      height={150}
                      style={{ objectFit: 'cover' }}
                      preview={false}
                    />
                    <div className="image-selector-overlay">
                      <Checkbox checked={isSelected} />
                    </div>
                    {isInGallery && (
                      <div className="in-gallery-badge">{t('imageSelector.inGallery')}</div>
                    )}
                    <div className="image-selector-info">
                      <div className="file-name" title={record.fileName}>
                        {record.fileName}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* 分页 */}
            <div style={{ textAlign: 'center', marginTop: 12 }}>
              <Pagination
                current={page}
                pageSize={pageSize}
                total={total}
                onChange={handlePageChange}
                showSizeChanger={false}
                showTotal={(total) => t('imageSelector.totalImages', { total })}
                size="small"
              />
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default ImageSelector;
